import { useSearchParams } from 'next/navigation';
import { type FC, useEffect, useRef } from 'react';

import { hasHtmlContent, removeUrlParameters } from '@/helpers/common';
import { ThemeMode } from '@/helpers/enums';
import { REF_PARAM } from '@/helpers/variables';
import { IconComponent } from '../IconComponent';
import { ScQuestionAnswerItem } from './styled';

const TOP_MARGIN = 48;
const SCROLL_DELAY = 200;

interface IQuestionAnswerItemProps {
  title: string;
  description?: string;
  identifier?: string;
  descriptionComponent?: JSX.Element;
  isOpen?: boolean;
  arrow?: boolean;
  mode?: ThemeMode;
  handleOpen: () => void;
  withScrollToActive?: boolean;
  className?: string;
}

export const QuestionAnswerItem: FC<IQuestionAnswerItemProps> = ({
  title,
  description,
  descriptionComponent,
  isOpen,
  arrow = false,
  mode = ThemeMode.Light,
  handleOpen,
  withScrollToActive,
  identifier,
  className,
}) => {
  const searchParams = useSearchParams();
  const itemParam = searchParams.get(REF_PARAM);

  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (itemParam && itemParam === identifier) {
      handleOpen();
      removeUrlParameters([REF_PARAM]);
    }
  }, [itemParam, identifier, handleOpen]);

  useEffect(() => {
    if (isOpen && withScrollToActive) {
      const timeoutId = setTimeout(
        () =>
          window.scrollTo({
            top:
              (itemRef?.current?.getBoundingClientRect().top || 0) +
              window.scrollY -
              TOP_MARGIN,
            behavior: 'smooth',
          }),
        SCROLL_DELAY
      );

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, withScrollToActive]);

  return (
    <ScQuestionAnswerItem
      arrow={arrow}
      className={className}
      data-selected={isOpen}
      isSelected={isOpen}
      mode={mode}
      ref={itemRef}
    >
      <div className="hover-elements" />
      <div className="question" onClick={handleOpen}>
        <span dangerouslySetInnerHTML={{ __html: title }} />
        {arrow ? (
          <div className="arrow">
            <IconComponent name="whiteArrowRight" size="18px" />
          </div>
        ) : (
          <div className="open-close-btn" />
        )}
      </div>
      <div className="answer-box">
        {description ? (
          hasHtmlContent(description) ? (
            <div
              className="answer"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          ) : (
            <div className="answer">
              <p>{description}</p>
            </div>
          )
        ) : descriptionComponent ? (
          <div className="answer">{descriptionComponent}</div>
        ) : null}
      </div>
    </ScQuestionAnswerItem>
  );
};
